import React from 'react';
import styled from 'styled-components';
import {ButtonDropdownField, DropdownList, tokensForThemeProvider} from '@forecast-it/design-system';
import {FormattedMessage} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {findRelativeDatePreset, RELATIVE_DATE_PRESETS} from '../relativeDatePresets';

const tokens = tokensForThemeProvider.sky.sys;

const PLACEHOLDER_MESSAGE_ID = 'ai_report_builder.date_preset_placeholder';
const CUSTOM_RANGE_MESSAGE_ID = 'ai_report_builder.date_preset_custom';
const ALL_TIME_MESSAGE_ID = 'ai_report_builder.period_all_time';

/* the design: this field carries the same 8px corner radius as the adjacent date-range
 * field (sky/sys/size/borderradius/3) — ButtonDropdownField's own default (borderradius/2, 4px) is
 * a shade tighter, so it's overridden here rather than forked. */
const RoundedButtonDropdownField = styled(ButtonDropdownField)`
	border-radius: ${tokens.size.borderradius['3']};
	/* The trigger sizes to its label rather than wrapping it. Every label here is a short phrase of
	 two or three words ("Select Date Range", "Custom range"), and the field's auto width otherwise
	 lets the flex row squeeze it until the label breaks onto a second line, which makes the control
	 taller than the ones beside it and misaligns the whole date group. */
	white-space: nowrap;
	flex-shrink: 0;
`;

/** The "Select Date Range" dropdown of relative period presets, the leftmost control in the
 * toolbar's date group (the scope selector and the absolute DateRangePicker follow it). Selecting
 * a preset resolves it and writes the result into the shared period state; the picker then shows
 * the resolved dates. Operates purely through useReportBuilder()'s period/setPeriodPreset - it
 * holds no local state of its own. */
export function DateRangePresetDropdown(): JSX.Element {
	const {period, setPeriodPreset, setPeriod} = useReportBuilder();
	const selected = findRelativeDatePreset(period.datePreset);
	// A report with neither a preset nor either date applies no date filter. Naming that state, rather
	// than showing the placeholder, keeps the edit control saying what the read-mode summary says.
	const hasNoRange = !selected && period.startDate === null && period.endDate === null;
	const labelId = selected?.labelId ?? (hasNoRange ? ALL_TIME_MESSAGE_ID : PLACEHOLDER_MESSAGE_ID);

	// Returns the report to having no date range at all: no preset, no dates, no date conditions.
	const clearToAllTime = (): void => {
		setPeriod(null, null);
	};

	// Detaches from the current preset without changing the dates the picker shows - the escape
	// hatch for "freeze exactly this range." Re-selecting the same dates via the date picker itself
	// wouldn't reliably fire a change (no-op if the value doesn't move), so this is the only direct
	// way to stop a period from rolling forward while keeping what it last resolved to.
	const clearToCustomRange = (): void => {
		setPeriod(period.startDate, period.endDate);
	};

	return (
		<DropdownList>
			<DropdownList.Trigger>
				{/* the design: the placeholder reads in the same dark main colour as every
				 * other toolbar label, not muted grey — isDefaultLabel is left off (it only affects
				 * text colour, per ButtonDropdownField's own styles) so the placeholder state never
				 * dims. */}
				<RoundedButtonDropdownField width={'auto'} data-cy={'ai-report-date-preset'}>
					<FormattedMessage id={labelId} />
				</RoundedButtonDropdownField>
			</DropdownList.Trigger>
			{/* align="start" (DropdownList.Content defaults to "end"): this dropdown is the leftmost
			 * control on the page, so an end-aligned menu would extend leftward past the trigger and
			 * off the edge of the viewport instead of into the open space to its right. */}
			<DropdownList.Content offset={4} align={'start'}>
				<DropdownList.Item onClick={clearToAllTime} data-cy={'ai-report-date-preset-all-time'}>
					<FormattedMessage id={ALL_TIME_MESSAGE_ID} />
				</DropdownList.Item>
				<DropdownList.Item onClick={clearToCustomRange} data-cy={'ai-report-date-preset-custom'}>
					<FormattedMessage id={CUSTOM_RANGE_MESSAGE_ID} />
				</DropdownList.Item>
				{RELATIVE_DATE_PRESETS.map(preset => (
					<DropdownList.Item
						key={preset.token}
						onClick={() => setPeriodPreset(preset.token)}
						data-cy={`ai-report-date-preset-option-${preset.token}`}
					>
						<FormattedMessage id={preset.labelId} />
					</DropdownList.Item>
				))}
			</DropdownList.Content>
		</DropdownList>
	);
}
